import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useAuth } from '../../../../hooks/useAuth';
import { useCompaniesQuery } from '../../../../hooks/queries/useCompaniesQuery';
import { useUpdateCompanyMutation } from '../../../../hooks/mutations/useCompanyMutation';
import { Company } from '../../../../services/apiSLTP/materials/companies';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { crudStatus, getCrudStatusInfos, localStorageKeys, permissionLevels } from '../../../../utils/enums';
import { getStatusColor } from '../../../../utils/colors';
import Icon from '../../../Icon';
import IconLine from '../../../IconLine';
import StateCell from '../../../cells/StateCell';
import DeleteModal from '../../../modals/DeleteModal';
import UpdateCompanyModal from '../../../modals/admin/external/UpdateCompanyModal';
import GenericAgGridTable from '../../GenericAgGridTable';
import { formatNumber } from '../../../../utils/numbers';
import IconButton from '../../../shared/IconButton';
import { createDateColumn, createDateTimeColumn } from '../../../../utils/dates';

const CompaniesSettingsRow: React.FC<{ withUsageCount: boolean }> = ({ withUsageCount }) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const { data: companies, isLoading, refetch } = useCompaniesQuery({ queries: { withUsage: withUsageCount } });

    const { mutate } = useUpdateCompanyMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating company:', error.message),
    });

    const handleEdit = (company: Company) => {
        setSelectedCompany(company);
        setIsEditMode(true);
        setShowEditModal(true);
    };

    const handleDelete = (company: Company) => {
        setSelectedCompany(company);
        setShowDeleteModal(true);
    };

    const columnDefs: ColDef<Company>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 250,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'codeProC',
            colId: 'codeproc',
        },

        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<Company>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modify'))}
                            onClick={() => handleEdit(params.data!)}
                            disabled={params.data.crudStatus === crudStatus.DELETED}
                        />
                        {user && user?.profileId.permsLevel <= permissionLevels.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('delete'))}
                                onClick={() => handleDelete(params.data!)}
                                disabled={params.data.crudStatus === crudStatus.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusInfos(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<Company>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getStatusColor(params.data?.crudStatus)} text={t(getCrudStatusInfos(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('address')),
            field: 'address',
            colId: 'address',
        },
        {
            headerName: capitalizeFirstLetter(t('latitude')),
            field: 'latitude',
            colId: 'latitude',
            valueFormatter: (params) => formatNumber(params.value, t('locale'), 8),
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('longitude')),
            field: 'longitude',
            colId: 'longitude',
            valueFormatter: (params) => formatNumber(params.value, t('locale'), 8),
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        /*{
            headerName: capitalizeFirstLetter(t('area')),
            field: 'area',
            colId: 'area',
        },*/

        {
            headerName: capitalizeFirstLetter(t('email')),
            field: 'email',
            colId: 'email',
        },
        {
            headerName: capitalizeFirstLetter(t('mobile')),
            field: 'phone',
            colId: 'phone',
        },
        /*{
            headerName: capitalizeFirstLetter(t('isOwnCompany')),
            field: 'isOwnCompany',
            colId: 'isOwnCompany',
        },
        {
            headerName: capitalizeFirstLetter(t('isGnrSupplier')),
            field: 'isGnrSupplier',
            colId: 'isGnrSupplier',
        },
        {
            headerName: capitalizeFirstLetter(t('isQuarry')),
            field: 'isQuarry',
            colId: 'isQuarry',
        },
        {
            headerName: capitalizeFirstLetter(t('isSiteSupplier')),
            field: 'isSiteSupplier',
            colId: 'isSiteSupplier',
        },
        {
            headerName: capitalizeFirstLetter(t('isSubcontractor')),
            field: 'isSubcontractor',
            colId: 'isSubcontractor',
        },
        {
            headerName: capitalizeFirstLetter(t('isTempWorker')),
            field: 'isTempWorker',
            colId: 'isTempWorker',
        },
        {
            headerName: capitalizeFirstLetter(t('isToolSupplier')),
            field: 'isToolSupplier',
            colId: 'isToolSupplier',
        },
        {
            headerName: capitalizeFirstLetter(t('isTransportCompany')),
            field: 'isTransportCompany',
            colId: 'isTransportCompany',
        },*/
        {
            headerName: capitalizeFirstLetter(t('createdBy')),
            field: 'createdBy',
            colId: 'createdBy',
        },
        createDateTimeColumn<Company>('createdAt', 'createdAt'),
        createDateTimeColumn<Company>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevels.ADMIN) {
        columnDefs.push(
            {
                headerName: capitalizeFirstLetter(t('_id')),
                field: '_id',
                minWidth: 250,
                colId: '_id',
                cellClass: 'usage-col-main',
                headerClass: 'usage-col-main',
            },
            {
                headerName: capitalizeFirstLetter(t('xTotal')),
                field: 'usageCount.total',
                filter: 'agNumberColumnFilter',
                colId: 'xtotal',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
            }
        );
    }

    return (
        <>
            <GenericAgGridTable
                columnDefs={columnDefs}
                rowData={companies || []}
                loading={isLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedCompany(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addCompany'))}
                    />
                }
                exportCsvFileName="admin-companies"
                storageKey={localStorageKeys.TABLE.ADMIN.EXTERNAL.COMPANIES}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateCompanyModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    company={selectedCompany}
                    isEditMode={isEditMode}
                    refetch={refetch}
                />
            )}

            {showDeleteModal && selectedCompany && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutate({ companyId: selectedCompany._id, data: { crudStatus: crudStatus.DELETED } });
                    }}
                    title={t('company')}
                    itemName={selectedCompany.designation}
                />
            )}
        </>
    );
};

export default CompaniesSettingsRow;
